import React, { useEffect, useReducer } from 'react'
import { Button, Dimmer, Form, Icon, Loader, Message } from 'semantic-ui-react'
import PropTypes from 'prop-types'
import '../SuperCutoff.css'

// Mappa numero mese (1-12) -> etichetta visibile, allineata con il pattern del progetto.
const MONTH_LABELS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

// ID del box di conferma per aria-describedby sul bottone di esecuzione
const CONFIRM_DESC_ID = 'bulk-approve-confirm-desc'

// Fasi del wizard — transizioni valide: IDLE -> PREVIEW -> CONFIRMING -> COMPLETED
// CONFIRMING -> PREVIEW (annullamento conferma)
const PHASE_IDLE = 'IDLE'
const PHASE_PREVIEW = 'PREVIEW'
const PHASE_CONFIRMING = 'CONFIRMING'
const PHASE_COMPLETED = 'COMPLETED'

const ACTION_SHOW_PREVIEW = 'SHOW_PREVIEW'
const ACTION_SHOW_CONFIRM = 'SHOW_CONFIRM'
const ACTION_CANCEL_CONFIRM = 'CANCEL_CONFIRM'
const ACTION_COMPLETE = 'COMPLETE'
// Segnala che il preview e stato richiesto — usato per la guard contro doppio click
const ACTION_PREVIEW_REQUESTED = 'PREVIEW_REQUESTED'

const initialWizardState = { phase: PHASE_IDLE, isPreviewPending: false }

function wizardReducer(state, action) {
  switch (action.type) {
    case ACTION_PREVIEW_REQUESTED:
      return { ...state, isPreviewPending: true }
    case ACTION_SHOW_PREVIEW:
      return { phase: PHASE_PREVIEW, isPreviewPending: false }
    case ACTION_SHOW_CONFIRM:
      if (state.phase !== PHASE_PREVIEW) return state
      return { ...state, phase: PHASE_CONFIRMING }
    case ACTION_CANCEL_CONFIRM:
      if (state.phase !== PHASE_CONFIRMING) return state
      return { ...state, phase: PHASE_PREVIEW }
    case ACTION_COMPLETE:
      return { phase: PHASE_COMPLETED, isPreviewPending: false }
    default:
      return state
  }
}

/**
 * Step 2+3 — Preview e approvazione massiva ore/spese.
 * Prima mostra il count (preview), poi permette di eseguire l'approvazione massiva.
 *
 * Il periodo non è scelto liberamente dall'utente: viene derivato dalla lista cutoff PM
 * caricata dal container e corrisponde al mese successivo all'ultimo super cutoff registrato.
 * Il payload inviato a onPreview/onExecute è { date_start, date_end } in formato YYYY-MM-DD,
 * preso direttamente da nextPeriod.
 *
 * Props:
 *   preview            {object}    [opzionale] Risultato preview { hours_to_approve, expenses_to_approve }
 *   result             {object}    [opzionale] Risultato execute { hours_approved, expenses_approved }
 *   nextPeriod         {object}    [opzionale] { month, year, date_start, date_end } derivato da cutoff_pm.
 *                                  Se null, lo step e bloccato: il messaggio di errore informa l'utente
 *                                  che non e possibile determinare il periodo target.
 *   loadingNextPeriod  {boolean}   [opzionale] True mentre la lista cutoff PM e in caricamento. Default: false
 *   onPreview          {Function}  Callback per caricare il preview. Firma: fn({ date_start, date_end })
 *   onExecute          {Function}  Callback per eseguire l'approvazione. Firma: fn({ date_start, date_end })
 *   loadingPreview     {boolean}   [opzionale] Default: false
 *   loadingExecute     {boolean}   [opzionale] Default: false
 */
const StepBulkApprove = ({
  preview,
  result,
  nextPeriod,
  loadingNextPeriod = false,
  onPreview,
  onExecute,
  loadingPreview = false,
  loadingExecute = false,
}) => {
  // isPreviewPending e gestito nel reducer per evitare divergenza con lo stato del wizard
  const [wizard, dispatchWizard] = useReducer(wizardReducer, initialWizardState)

  // Quando il parent segnala che il preview e arrivato, avanziamo la fase e resettiamo la guard
  useEffect(() => {
    if (preview) {
      dispatchWizard({ type: ACTION_SHOW_PREVIEW })
    }
  }, [preview])

  // Quando il parent segnala che l'esecuzione e completata, avanziamo alla fase finale
  useEffect(() => {
    if (result) {
      dispatchWizard({ type: ACTION_COMPLETE })
    }
  }, [result])

  // Periodo derivato dal nextPeriod: nessun calcolo locale.
  const hasNextPeriod = nextPeriod !== null && nextPeriod !== undefined
  const month = hasNextPeriod ? nextPeriod.month : null
  const year = hasNextPeriod ? nextPeriod.year : null
  const monthLabel = (month !== null) ? MONTH_LABELS[month - 1] : ''

  // Le opzioni delle select contengono SOLO il valore vincolato (Q2 = a):
  // l'utente vede esplicitamente il mese e l'anno target, ma non puo' cambiarli.
  // Se nextPeriod e null le select sono vuote (le mostriamo comunque per coerenza visiva,
  // ma i bottoni sotto sono disabilitati e un Message segnala il blocco).
  const monthOptions = hasNextPeriod
    ? [{ key: month, value: month, text: monthLabel }]
    : []
  const yearOptions = hasNextPeriod
    ? [{ key: year, value: year, text: String(year) }]
    : []

  const handlePreview = () => {
    if (typeof onPreview !== 'function') return
    if (!hasNextPeriod) return
    dispatchWizard({ type: ACTION_PREVIEW_REQUESTED })
    onPreview({ date_start: nextPeriod.date_start, date_end: nextPeriod.date_end })
  }

  const handleExecute = () => {
    if (typeof onExecute !== 'function') return
    if (!hasNextPeriod) return
    dispatchWizard({ type: ACTION_CANCEL_CONFIRM })
    onExecute({ date_start: nextPeriod.date_start, date_end: nextPeriod.date_end })
  }

  const handleShowConfirm = () => {
    dispatchWizard({ type: ACTION_SHOW_CONFIRM })
  }

  const handleCancelConfirm = () => {
    dispatchWizard({ type: ACTION_CANCEL_CONFIRM })
  }

  const isIdle = wizard.phase === PHASE_IDLE
  const isPreview = wizard.phase === PHASE_PREVIEW
  const isConfirming = wizard.phase === PHASE_CONFIRMING
  const isCompleted = wizard.phase === PHASE_COMPLETED
  const isPreviewPending = wizard.isPreviewPending

  // Gating dei bottoni: bloccati anche durante il loading del periodo o se non disponibile.
  const periodReady = hasNextPeriod && !loadingNextPeriod
  const periodBlocked = !loadingNextPeriod && !hasNextPeriod

  return (
    <div>
      <p className="supercutoff-step-description">
        Bulk-approves all pending hours and expenses for the target month.
        The period is locked to the month following the latest super cutoff: first load the
        preview to see how many records will be affected, then confirm the execution.
      </p>

      {/* Loader sulla selezione finche cutoff_pm non e arrivato */}
      {loadingNextPeriod && (
        <div className="supercutoff-period-loader">
          <Dimmer active inverted>
            <Loader inverted content="Loading target period" size="small" />
          </Dimmer>
        </div>
      )}

      {/* Errore bloccante: cutoff_pm caricato ma vuoto/non valido */}
      {periodBlocked && (
        <Message negative icon>
          <Icon name="ban" aria-hidden="true" />
          <Message.Content>
            <Message.Header>Cannot determine target period</Message.Header>
            <p>
              The super cutoff list is unavailable or empty. Reload the page to retry, or
              contact the administrator if the problem persists.
            </p>
          </Message.Content>
        </Message>
      )}

      {/* Form selezione periodo — visibile solo nella fase IDLE quando il periodo e disponibile */}
      {isIdle && periodReady && (
        <Form className="supercutoff-form-wrapper" aria-label="Selected period (locked)">
          <Form.Group widths="equal">
            <Form.Select
              id="bulk-approve-month-select"
              label="Month"
              options={monthOptions}
              value={month}
              disabled
            />
            <Form.Select
              id="bulk-approve-year-select"
              label="Year"
              options={yearOptions}
              value={year}
              disabled
            />
          </Form.Group>
        </Form>
      )}

      {/* Preview risultati — visibile nelle fasi PREVIEW e CONFIRMING */}
      {(isPreview || isConfirming) && preview && (
        <div className="supercutoff-preview-box">
          <h4>Preview — records to approve</h4>
          <div className="supercutoff-preview-stats">
            <div className="supercutoff-stat">
              <span className="supercutoff-stat-value">
                {(preview.hours_to_approve || 0).toLocaleString()}
              </span>
              <span className="supercutoff-stat-label">Hours pending</span>
            </div>
            <div className="supercutoff-stat">
              <span className="supercutoff-stat-value">
                {(preview.expenses_to_approve || 0).toLocaleString()}
              </span>
              <span className="supercutoff-stat-label">Expenses pending</span>
            </div>
          </div>
        </div>
      )}

      {/* Conferma esecuzione — role="alert" annuncia la comparsa agli screen reader */}
      {isConfirming && preview && (
        <div className="supercutoff-confirm-box" role="alert">
          <Icon name="warning sign" color="yellow" size="large" aria-hidden="true" />
          <span id={CONFIRM_DESC_ID} className="confirm-text">
            You are about to approve <strong>{preview.hours_to_approve} hours</strong> and{' '}
            <strong>{preview.expenses_to_approve} expenses</strong> for{' '}
            <strong>{monthLabel} {year}</strong> with approver "Approved by SuperCutoff".
            This action cannot be undone.
          </span>
        </div>
      )}

      {/* Risultato esecuzione — visibile nella fase COMPLETED */}
      {isCompleted && result && (
        <Message positive icon>
          <Icon name="check circle" aria-hidden="true" />
          <Message.Content>
            <Message.Header>Bulk approval completed</Message.Header>
            <p>
              <strong>{(result.hours_approved || 0).toLocaleString()}</strong> hours and{' '}
              <strong>{(result.expenses_approved || 0).toLocaleString()}</strong> expenses approved successfully.
            </p>
          </Message.Content>
        </Message>
      )}

      <div className="supercutoff-step-actions">
        {isIdle && (
          <Button
            primary
            icon
            labelPosition="left"
            onClick={handlePreview}
            loading={loadingPreview || isPreviewPending}
            disabled={loadingPreview || isPreviewPending || !periodReady}
          >
            <Icon name="eye" aria-hidden="true" />
            Load preview
          </Button>
        )}

        {isPreview && (
          <Button
            color="orange"
            icon
            labelPosition="left"
            onClick={handleShowConfirm}
            disabled={loadingExecute || !periodReady}
          >
            <Icon name="play" aria-hidden="true" />
            Execute bulk approval
          </Button>
        )}

        {isConfirming && (
          <React.Fragment>
            <Button
              color="red"
              icon
              labelPosition="left"
              onClick={handleExecute}
              loading={loadingExecute}
              disabled={loadingExecute || !periodReady}
              aria-describedby={CONFIRM_DESC_ID}
            >
              <Icon name="check" aria-hidden="true" />
              Confirm execution
            </Button>
            <Button basic onClick={handleCancelConfirm} disabled={loadingExecute}>
              Cancel
            </Button>
          </React.Fragment>
        )}

        {isCompleted && (
          <span className="supercutoff-status-badge completed">
            <Icon name="check" aria-hidden="true" />
            Step completed
          </span>
        )}
      </div>
    </div>
  )
}

StepBulkApprove.propTypes = {
  preview: PropTypes.shape({
    hours_to_approve: PropTypes.number.isRequired,
    expenses_to_approve: PropTypes.number.isRequired,
  }),
  result: PropTypes.shape({
    hours_approved: PropTypes.number.isRequired,
    expenses_approved: PropTypes.number.isRequired,
  }),
  nextPeriod: PropTypes.shape({
    month: PropTypes.number.isRequired,
    year: PropTypes.number.isRequired,
    date_start: PropTypes.string.isRequired,
    date_end: PropTypes.string.isRequired,
  }),
  loadingNextPeriod: PropTypes.bool,
  onPreview: PropTypes.func.isRequired,
  onExecute: PropTypes.func.isRequired,
  loadingPreview: PropTypes.bool,
  loadingExecute: PropTypes.bool,
}

export default StepBulkApprove
